import AssignmentService from './assignmentService';

class AssignmentTestAutomation {
  constructor() {
    this.isDevelopmentMode = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
    this.testScenarios = [];
    this.currentTestIndex = 0;
    this.isRunning = false;
    this.testResults = [];
    this.debugMode = false;
  }

  // Test data generators
  generateTestData(scenario = 'default') {
    const baseData = {
      assetId: 'AST-007',
      assetName: 'Dell Latitude 5520',
      assetType: 'Laptop',
      assignedToId: '507f1f77bcf86cd799439011',
      assignedTo: 'John Boy',
      assignedToEmail: 'john.boy@company.com',
      assignedToPhone: '+1-555-0123',
      department: 'IT',
      assignedDate: new Date().toISOString().split('T')[0],
      expectedReturnDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      location: 'Office Floor 2, Desk 15',
      purpose: 'Development Work',
      condition: 'Good',
      notes: 'Primary development machine for React projects',
      assignedBy: 'Test User'
    };

    const scenarios = {
      default: baseData,
      invalid: {
        ...baseData,
        assetId: '',
        assignedToId: '',
        department: '',
        assignedDate: 'invalid-date',
        expectedReturnDate: '2020-01-01' // Past date
      },
      edgeCase: {
        ...baseData,
        purpose: 'A'.repeat(250), // Too long
        notes: 'B'.repeat(600), // Too long
        location: 'C'.repeat(150) // Too long
      },
      valid: {
        ...baseData,
        assetId: 'AST-001',
        assignedToId: '507f1f77bcf86cd799439012',
        department: 'Operations',
        purpose: 'Client Meetings',
        notes: 'For client presentations and meetings'
      }
    };

    return scenarios[scenario] || baseData;
  }

  // Auto-fill form with test data
  async autoFillForm(scenario = 'default', formRef = null) {
    if (!this.isDevelopmentMode) {
      throw new Error('Auto-fill is only available in development mode');
    }

    const testData = this.generateTestData(scenario);
    
    // If formRef is provided, fill the form directly
    if (formRef && formRef.current) {
      const form = formRef.current;
      Object.keys(testData).forEach(key => {
        const input = form.querySelector(`[name="${key}"]`);
        if (input) {
          input.value = testData[key];
          // Trigger change event
          const event = new Event('change', { bubbles: true });
          input.dispatchEvent(event);
        }
      });
    }

    return testData;
  }

  // Create test assignment
  async createTestAssignment(scenario = 'default') {
    if (!this.isDevelopmentMode) {
      throw new Error('Test creation is only available in development mode');
    }

    const testData = this.generateTestData(scenario);
    
    try {
      const response = await AssignmentService.createAssignment(testData);
      return {
        success: true,
        data: response.data,
        testData
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        testData
      };
    }
  }

  // Validate form data
  async validateForm(formData) {
    const validationResults = {
      isValid: true,
      errors: [],
      warnings: [],
      score: 100
    };

    // Required field validation
    const requiredFields = ['assetId', 'assignedToId', 'department', 'assignedDate', 'location', 'purpose'];
    requiredFields.forEach(field => {
      if (!formData[field] || formData[field].toString().trim() === '') {
        validationResults.errors.push({
          field,
          message: `${field} is required`,
          type: 'required'
        });
        validationResults.score -= 15;
      }
    });

    // Date validation
    if (formData.assignedDate) {
      const assignedDate = new Date(formData.assignedDate);
      if (isNaN(assignedDate.getTime())) {
        validationResults.errors.push({
          field: 'assignedDate',
          message: 'Invalid assignment date format',
          type: 'format'
        });
        validationResults.score -= 20;
      }
    }

    if (formData.expectedReturnDate) {
      const returnDate = new Date(formData.expectedReturnDate);
      if (isNaN(returnDate.getTime())) {
        validationResults.errors.push({
          field: 'expectedReturnDate',
          message: 'Invalid expected return date format',
          type: 'format'
        });
        validationResults.score -= 20;
      } else if (formData.assignedDate) {
        const assignedDate = new Date(formData.assignedDate);
        if (returnDate <= assignedDate) {
          validationResults.errors.push({
            field: 'expectedReturnDate',
            message: 'Expected return date must be after assignment date',
            type: 'logic'
          });
          validationResults.score -= 25;
        }
      }
    }

    // Email validation
    if (formData.assignedToEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.assignedToEmail)) {
        validationResults.errors.push({
          field: 'assignedToEmail',
          message: 'Invalid email format',
          type: 'format'
        });
        validationResults.score -= 10;
      }
    }

    // Length validations
    if (formData.purpose && formData.purpose.length > 200) {
      validationResults.errors.push({
        field: 'purpose',
        message: 'Purpose must be less than 200 characters',
        type: 'length'
      });
      validationResults.score -= 10;
    }

    if (formData.notes && formData.notes.length > 500) {
      validationResults.errors.push({
        field: 'notes',
        message: 'Notes must be less than 500 characters',
        type: 'length'
      });
      validationResults.score -= 5;
    }

    validationResults.isValid = validationResults.errors.length === 0;
    validationResults.score = Math.max(0, validationResults.score);

    return validationResults;
  }

  // Reset form to initial state
  resetForm(formRef = null) {
    if (!this.isDevelopmentMode) {
      throw new Error('Form reset is only available in development mode');
    }

    const defaultData = {
      assetId: '',
      assignedToId: '',
      department: '',
      assignedDate: '',
      expectedReturnDate: '',
      location: '',
      purpose: '',
      notes: ''
    };

    if (formRef && formRef.current) {
      const form = formRef.current;
      Object.keys(defaultData).forEach(key => {
        const input = form.querySelector(`[name="${key}"]`);
        if (input) {
          input.value = defaultData[key];
          // Trigger change event
          const event = new Event('change', { bubbles: true });
          input.dispatchEvent(event);
        }
      });
    }

    return defaultData;
  }

  // Debug mode - log detailed information
  enableDebugMode() {
    this.debugMode = true;
    console.log('🐛 Debug mode enabled for Assignment Test Automation');
  }

  disableDebugMode() {
    this.debugMode = false;
    console.log('🐛 Debug mode disabled');
  }

  debugLog(message, data = null) {
    if (this.debugMode) {
      console.log(`🐛 [Assignment Test] ${message}`, data);
    }
  }

  // Run comprehensive test suite
  async runTestSuite() {
    if (!this.isDevelopmentMode) {
      throw new Error('Test suite is only available in development mode');
    }

    this.isRunning = true;
    this.testResults = [];
    this.debugLog('Starting comprehensive test suite...');

    const testScenarios = [
      { name: 'Valid Assignment Creation', scenario: 'valid' },
      { name: 'Invalid Data Validation', scenario: 'invalid' },
      { name: 'Edge Case Handling', scenario: 'edgeCase' },
      { name: 'Default Data Test', scenario: 'default' }
    ];

    for (const test of testScenarios) {
      this.debugLog(`Running test: ${test.name}`);
      
      try {
        // Generate test data
        const testData = this.generateTestData(test.scenario);
        this.debugLog(`Test data generated:`, testData);

        // Validate the data
        const validation = await this.validateForm(testData);
        this.debugLog(`Validation result:`, validation);

        // Try to create assignment (only for valid scenarios)
        let creationResult = null;
        if (test.scenario === 'valid' || test.scenario === 'default') {
          creationResult = await this.createTestAssignment(test.scenario);
          this.debugLog(`Creation result:`, creationResult);
        }

        this.testResults.push({
          testName: test.name,
          scenario: test.scenario,
          testData,
          validation,
          creationResult,
          timestamp: new Date().toISOString(),
          status: validation.isValid ? 'pass' : 'fail'
        });

      } catch (error) {
        this.debugLog(`Test failed: ${test.name}`, error);
        this.testResults.push({
          testName: test.name,
          scenario: test.scenario,
          error: error.message,
          timestamp: new Date().toISOString(),
          status: 'error'
        });
      }
    }

    this.isRunning = false;
    this.debugLog('Test suite completed', this.testResults);

    return {
      success: this.testResults.every(r => r.status === 'pass'),
      totalTests: this.testResults.length,
      passed: this.testResults.filter(r => r.status === 'pass').length,
      failed: this.testResults.filter(r => r.status === 'fail').length,
      errors: this.testResults.filter(r => r.status === 'error').length,
      results: this.testResults
    };
  }

  // Simulate user interaction sequence
  async simulateUserInteraction(sequence = 'createAssignment') {
    if (!this.isDevelopmentMode) {
      throw new Error('User simulation is only available in development mode');
    }

    const sequences = {
      createAssignment: [
        { action: 'click', target: 'New Assignment button' },
        { action: 'fill', field: 'assetId', value: 'AST-007' },
        { action: 'fill', field: 'assignedToId', value: '507f1f77bcf86cd799439011' },
        { action: 'fill', field: 'department', value: 'IT' },
        { action: 'fill', field: 'assignedDate', value: new Date().toISOString().split('T')[0] },
        { action: 'fill', field: 'location', value: 'Office Floor 2, Desk 15' },
        { action: 'fill', field: 'purpose', value: 'Development Work' },
        { action: 'click', target: 'Submit button' }
      ],
      editAssignment: [
        { action: 'click', target: 'Edit button for first assignment' },
        { action: 'fill', field: 'location', value: 'Updated Location' },
        { action: 'fill', field: 'notes', value: 'Updated notes' },
        { action: 'click', target: 'Update button' }
      ],
      validateForm: [
        { action: 'click', target: 'New Assignment button' },
        { action: 'click', target: 'Submit button' }, // Submit empty form
        { action: 'verify', target: 'validation errors' }
      ]
    };

    const steps = sequences[sequence] || sequences.createAssignment;
    const results = [];

    for (const step of steps) {
      this.debugLog(`Executing step: ${step.action} - ${step.target}`);
      
      try {
        // Simulate the action
        const result = await this.executeAction(step);
        results.push({
          step,
          result,
          timestamp: new Date().toISOString()
        });
        
        // Add delay between actions to simulate real user behavior
        await new Promise(resolve => setTimeout(resolve, 500));
        
      } catch (error) {
        results.push({
          step,
          error: error.message,
          timestamp: new Date().toISOString()
        });
      }
    }

    return results;
  }

  // Execute individual action
  async executeAction(action) {
    switch (action.action) {
      case 'click':
        return { message: `Clicked ${action.target}` };
      case 'fill':
        return { message: `Filled ${action.field} with ${action.value}` };
      case 'verify':
        return { message: `Verified ${action.target}` };
      default:
        return { message: `Executed ${action.action}` };
    }
  }

  // Generate test report
  generateTestReport() {
    const report = {
      summary: {
        totalTests: this.testResults.length,
        passed: this.testResults.filter(r => r.status === 'pass').length,
        failed: this.testResults.filter(r => r.status === 'fail').length,
        errors: this.testResults.filter(r => r.status === 'error').length,
        timestamp: new Date().toISOString()
      },
      results: this.testResults,
      debugMode: this.debugMode,
      isDevelopmentMode: this.isDevelopmentMode
    };

    return report;
  }

  // Export test results
  exportTestResults() {
    const report = this.generateTestReport();
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `assignment-test-automation-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export default new AssignmentTestAutomation();
